// ---------------------------------------------------------------------------
// Table types — named game setups (Commander, Duel, Poker, Checkers…).
//
// Each TableSpec is lobby metadata plus a pure build() that produces the
// room's starting objects. Builders run on the server; the metadata renders
// the client's table browser. Shared layout constants (playmat slots, the
// checkerboard grid) live here so face art and zone placement cannot drift
// apart. Isomorphic: no DOM, no Bun APIs.
// ---------------------------------------------------------------------------

import { CHESS_PIECES, CHIP_COLORS, DICE, DMG_COUNTERS, getDef, RANKS, SUITS, type ChessPieceKind } from "./defs";
import { shuffleInPlace, type Rng } from "./math";
import { restY, type GameObject, type Item, type SlotKind } from "./state";

export interface TableSize {
  w: number;
  d: number;
}

export interface TableBuildCtx {
  newId(): string;
  /** Register an object; fills in resting y (when pos.y is 0) and count. */
  add(o: GameObject): GameObject;
  rng: Rng;
  size: TableSize;
}

export interface TableSpec {
  id: string;
  name: string;
  tagline: string;
  category: "Sandbox" | "Trading card games" | "Card games" | "Board games";
  /** Player count shown in the browser, e.g. "2–4". */
  players: string;
  /** Accent color for the lobby card. */
  accent: string;
  icon: string;
  table: TableSize;
  /** Life counter config; absent = no life panel. */
  life?: { start: number; seats: number; commander: boolean };
  /** Suggested per-player deal size (shown first on the deck's deal menu). */
  deal?: number;
  /**
   * Game-action ids for the client's action bar (see client/actions.ts).
   * Order = button order. Absent = no action bar on this table.
   */
  actions?: readonly string[];
  build(ctx: TableBuildCtx): void;
}

// ------------------------------------------------------------ shared layout

/**
 * MTG playmat layout, mat-local units (+x right, +z toward the player when
 * yaw = 0). Face art (client) and slot zones (server) both read this.
 *
 * v2: a flat mat with the four zones stacked in one vertical column on the
 * right — command (far) → exile → graveyard → library (nearest the player,
 * where the drawing hand lives). The rest of the mat is open battlefield.
 */
export const MTG_MAT = {
  def: "board.mtgmat2",
  w: 68,
  d: 50,
  slotW: 7.6,
  slotD: 10.2,
  /** Mat-local x of the zone column. */
  colX: 27.5,
  slots: [
    { slot: "command", label: "cmdr", lx: 27.5, lz: -18.9 },
    { slot: "exile", label: "exile", lx: 27.5, lz: -6.3 },
    { slot: "graveyard", label: "grave", lx: 27.5, lz: 6.3 },
    { slot: "library", label: "library", lx: 27.5, lz: 18.9 },
  ] as Array<{ slot: "library" | "graveyard" | "exile" | "command"; label: string; lx: number; lz: number }>,
} as const;

/** The v1 slab mat's layout — old saved rooms still render this def. */
export const MTG_MAT_LEGACY = {
  def: "board.mtgmat",
  w: 64,
  d: 42,
  slotW: 7.6,
  slotD: 10.2,
  slots: [
    { slot: "library", label: "library", lx: 27, lz: 14 },
    { slot: "graveyard", label: "grave", lx: 27, lz: 2 },
    { slot: "exile", label: "exile", lx: 27, lz: -10 },
    { slot: "command", label: "cmdr", lx: 18, lz: 14 },
  ] as Array<{ slot: "library" | "graveyard" | "exile" | "command"; label: string; lx: number; lz: number }>,
} as const;

/**
 * Checkerboard grid (must match the proc-board "checker" face drawing:
 * 8×8 cells inside a 4% margin). Row 0 / col 0 is the -x/-z corner; the face
 * canvas maps to world axes directly, so dark squares are (col+row) odd —
 * verified against a top-down screenshot.
 */
export const CHECKERBOARD = {
  def: "board.checker",
  size: 48,
  cells: 8,
  margin: 48 * 0.04,
} as const;

/** Center of a checkerboard square in board-local units. */
export function checkerSquare(col: number, row: number): { x: number; z: number } {
  const cell = (CHECKERBOARD.size - 2 * CHECKERBOARD.margin) / CHECKERBOARD.cells;
  const org = -CHECKERBOARD.size / 2 + CHECKERBOARD.margin + cell / 2;
  return { x: org + col * cell, z: org + row * cell };
}

/**
 * Chess back rank, cols 0→7. The south seat (+z, white) sees col 0 on its
 * left, so cols are files a–h and this puts both queens on the d-file —
 * white queen on a light square, black on dark, as the rules demand.
 */
export const CHESS_BACK_RANK: readonly ChessPieceKind[] =
  ["rook", "knight", "bishop", "queen", "king", "bishop", "knight", "rook"];

/**
 * Backgammon board layout, board-local units (+z toward the south player).
 * Points are numbered 1–24 from the south player's view: 1 at the near-right
 * corner running right→left along the south row, 13–24 mirrored along the
 * north row (so point 13 sits directly across the bar from point 12). Face
 * art (client) and checker placement (server) both read this.
 */
export const BACKGAMMON = {
  def: "board.backgammon",
  w: 76,
  d: 60,
  frame: 3,       // wooden rail width
  bar: 6,         // center bar width
  pointDepth: 23, // triangle length from the rail toward the middle
} as const;

/** Width of one backgammon point (six per quadrant). */
export function bgPointPitch(): number {
  return (BACKGAMMON.w / 2 - BACKGAMMON.frame - BACKGAMMON.bar / 2) / 6;
}

/** Center x of a backgammon point, board-local units. */
export function bgPointX(point: number): number {
  const i = point <= 12 ? point - 1 : 24 - point; // 0..11 from the right, both rows
  const pitch = bgPointPitch();
  return i < 6
    ? BACKGAMMON.w / 2 - BACKGAMMON.frame - (i + 0.5) * pitch
    : -BACKGAMMON.bar / 2 - (i - 6 + 0.5) * pitch;
}

/** Board-local center of the j-th checker (0-based, rail outward) on a point. */
export function bgCheckerPos(point: number, j: number): { x: number; z: number } {
  const r = getDef("checker.red").size[0]; // checker diameter
  const edge = BACKGAMMON.d / 2 - BACKGAMMON.frame;
  const off = edge - r / 2 - j * r;
  return { x: bgPointX(point), z: point <= 12 ? off : -off };
}

/**
 * Standard backgammon start, south player's points: 2 on the 24-point,
 * 5 on 13, 3 on 8, 5 on 6. The opponent mirrors on 25−p.
 */
export const BG_START: ReadonlyArray<readonly [point: number, count: number]> =
  [[24, 2], [13, 5], [8, 3], [6, 5]];

/**
 * Trick-taking mat layout, mat-local units. One card slot per seat (S, N, W,
 * E — same order as edgeHandZones), each yawed toward its player so the
 * played card lands upright for whoever played it. Face art (client) and
 * slot zones (server) both read this.
 */
export const TRICK_MAT = {
  def: "board.trickmat",
  size: 46,
  slotW: 7.6,
  slotD: 10.2,
  slots: [
    { seat: 0, lx: 0, lz: 13.5, yaw: 0 },
    { seat: 1, lx: 0, lz: -13.5, yaw: Math.PI },
    { seat: 2, lx: -13.5, lz: 0, yaw: Math.PI / 2 },
    { seat: 3, lx: 13.5, lz: 0, yaw: -Math.PI / 2 },
  ] as Array<{ seat: number; lx: number; lz: number; yaw: number }>,
} as const;

/**
 * Keyed TCG-mat slot: `key` makes the object id unique (bench has five slots
 * that all share slot kind "bench"), `slot` drives pile behavior, `label` is
 * what the face art prints.
 */
export interface TcgMatSlot {
  key: string;
  slot: SlotKind;
  label: string;
  lx: number;
  lz: number;
}

export interface TcgMatSpec {
  def: string;
  w: number;
  d: number;
  slotW: number;
  slotD: number;
  slots: readonly TcgMatSlot[];
}

/**
 * Pokémon playmat layout, mat-local units (+x right, +z toward the player at
 * yaw = 0): active spot facing the opponent, five bench slots in front of the
 * player, deck/discard on the right, prizes on the left. Face art (client)
 * and slot zones (server) both read this.
 */
export const POKE_MAT: TcgMatSpec = {
  def: "board.pokemat",
  w: 66,
  d: 32,
  slotW: 7.6,
  slotD: 10.2,
  slots: [
    { key: "active", slot: "active", label: "active", lx: 0, lz: -8 },
    ...[-18, -9, 0, 9, 18].map((lx, i): TcgMatSlot => ({ key: `bench${i}`, slot: "bench", label: "bench", lx, lz: 8 })),
    { key: "library", slot: "library", label: "deck", lx: 27, lz: 8 },
    { key: "graveyard", slot: "graveyard", label: "discard", lx: 27, lz: -8 },
    { key: "prize", slot: "prize", label: "prizes", lx: -27, lz: -8 },
  ],
};

/**
 * Lorcana playmat layout, same conventions as POKE_MAT: deck/discard on the
 * right, the inkwell on the left (its face-down pile count IS your ink), the
 * open middle is the play area.
 */
export const LORCANA_MAT: TcgMatSpec = {
  def: "board.lorcanamat",
  w: 66,
  d: 32,
  slotW: 7.6,
  slotD: 10.2,
  slots: [
    { key: "library", slot: "library", label: "deck", lx: 27, lz: 8 },
    { key: "graveyard", slot: "graveyard", label: "discard", lx: 27, lz: -8 },
    { key: "inkwell", slot: "inkwell", label: "inkwell", lx: -27, lz: 8 },
  ],
};

// ---------------------------------------------------------------- builders

/** Edge hand-zone placements for the first `n` seats (S, N, W, E). */
export function edgeHandZones(size: TableSize, n: number): Array<{ x: number; z: number; yaw: number }> {
  return [
    { x: 0, z: size.d / 2 - 8.5, yaw: 0 },
    { x: 0, z: -size.d / 2 + 8.5, yaw: Math.PI },
    { x: -size.w / 2 + 9, z: 0, yaw: Math.PI / 2 },
    { x: size.w / 2 - 9, z: 0, yaw: -Math.PI / 2 },
  ].slice(0, n);
}

function addHandZones(ctx: TableBuildCtx, zones: Array<{ x: number; z: number; yaw: number }>): void {
  zones.forEach((zs, seat) => {
    ctx.add({
      id: `zone.s${seat}`,
      def: "zone.hand",
      pos: { x: zs.x, y: 0, z: zs.z },
      yaw: zs.yaw,
      faceUp: true,
      locked: true,
      seat,
    });
  });
}

export function standardDeckItems(rng?: Rng): Item[] {
  const items: Item[] = [];
  for (const suit of SUITS) for (const rank of RANKS) items.push({ def: `card.${rank}${suit}` });
  if (rng) shuffleInPlace(items, rng);
  return items;
}

/** The 28 tiles of a double-six domino set. */
export function dominoItems(rng?: Rng): Item[] {
  const items: Item[] = [];
  for (let a = 0; a <= 6; a++) for (let b = a; b <= 6; b++) items.push({ def: `domino.${a}${b}` });
  if (rng) shuffleInPlace(items, rng);
  return items;
}

function addDeck52(ctx: TableBuildCtx, x: number, z: number): GameObject {
  return ctx.add({
    id: ctx.newId(),
    def: "deck.standard",
    pos: { x, y: 0, z },
    yaw: 0,
    faceUp: false,
    contents: standardDeckItems(ctx.rng),
  });
}

function addChipBank(ctx: TableBuildCtx, x: number, z: number, perStack: number, colors?: string[]): void {
  (colors ?? CHIP_COLORS.map((c) => c.id)).forEach((c, i) => {
    ctx.add({
      id: ctx.newId(),
      def: `chipstack.${c}`,
      pos: { x: x + i * 6.5, y: 0, z },
      yaw: 0,
      faceUp: true,
      contents: Array.from({ length: perStack }, (): Item => ({ def: `chip.${c}` })),
    });
  });
}

/** Table-edge clearance from a mat's near edge to the felt edge: the hand
 *  zone (13 deep, centered 8.5 in) plus a visual gap. */
export const MTG_MAT_CLEARANCE = 16;

/** An MTG playmat for `seat` plus its slot zones, rotated by yaw. */
function addPlaymat(ctx: TableBuildCtx, seat: number, x: number, z: number, yaw: number): void {
  ctx.add({
    id: `mat.s${seat}`,
    def: MTG_MAT.def,
    pos: { x, y: 0, z },
    yaw,
    faceUp: true,
    locked: true,
    seat,
  });
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  for (const sl of MTG_MAT.slots) {
    ctx.add({
      id: `slot.s${seat}.${sl.slot}`,
      def: "zone.slot",
      pos: { x: x + sl.lx * cos - sl.lz * sin, y: 0, z: z + sl.lx * sin + sl.lz * cos },
      yaw,
      faceUp: true,
      locked: true,
      seat,
      slot: sl.slot,
    });
  }
}

/** A keyed TCG mat (Pokémon/Lorcana) for `seat` plus its slot zones. */
function addTcgMat(ctx: TableBuildCtx, mat: TcgMatSpec, seat: number, x: number, z: number, yaw: number): void {
  ctx.add({
    id: `mat.s${seat}`,
    def: mat.def,
    pos: { x, y: 0, z },
    yaw,
    faceUp: true,
    locked: true,
    seat,
  });
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  for (const sl of mat.slots) {
    ctx.add({
      id: `slot.s${seat}.${sl.key}`,
      def: "zone.slot",
      pos: { x: x + sl.lx * cos - sl.lz * sin, y: 0, z: z + sl.lx * sin + sl.lz * cos },
      yaw,
      faceUp: true,
      locked: true,
      seat,
      slot: sl.slot,
    });
  }
}

// ------------------------------------------------------------- the registry

export const TABLE_TYPES: TableSpec[] = [
  {
    id: "blank",
    name: "Open Table",
    tagline: "Empty felt with a deck and chips — build any game you like.",
    category: "Sandbox",
    players: "1–6",
    accent: "#3d8fe5",
    icon: "♠",
    table: { w: 170, d: 110 },
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 4));
      addDeck52(ctx, -35, -10);
      addChipBank(ctx, 14, -12, 20);
    },
  },
  {
    id: "commander",
    name: "Commander",
    tagline: "Four playmats, two a side, around a shared battlefield. 40 life, commander damage, import decks straight onto the mats.",
    category: "Trading card games",
    players: "2–4",
    accent: "#d8b45a",
    icon: "⚔",
    table: { w: 180, d: 150 },
    life: { start: 40, seats: 4, commander: true },
    actions: ["draw", "draw7", "mulligan", "untap", "toGrave", "toExile", "toHand", "shuffleLib"],
    build(ctx) {
      // Two seats per long side: 0/1 south, 2/3 north, mats side by side.
      const hz = ctx.size.d / 2 - 8.5;
      const mz = ctx.size.d / 2 - MTG_MAT_CLEARANCE - MTG_MAT.d / 2;
      const seats = [
        { seat: 0, x: -38, z: hz, mz, yaw: 0 },
        { seat: 1, x: 38, z: hz, mz, yaw: 0 },
        { seat: 2, x: -38, z: -hz, mz: -mz, yaw: Math.PI },
        { seat: 3, x: 38, z: -hz, mz: -mz, yaw: Math.PI },
      ];
      addHandZones(ctx, seats.map((s) => ({ x: s.x, z: s.z, yaw: s.yaw })));
      for (const s of seats) addPlaymat(ctx, s.seat, s.x, s.mz, s.yaw);
    },
  },
  {
    id: "duel",
    name: "MTG Duel",
    tagline: "Head-to-head Magic — two playmats, 20 life.",
    category: "Trading card games",
    players: "2",
    accent: "#a45de0",
    icon: "⚡",
    table: { w: 170, d: 140 },
    life: { start: 20, seats: 2, commander: false },
    actions: ["draw", "draw7", "mulligan", "untap", "toGrave", "toExile", "toHand", "shuffleLib"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 2));
      const mz = ctx.size.d / 2 - MTG_MAT_CLEARANCE - MTG_MAT.d / 2;
      addPlaymat(ctx, 0, 0, mz, 0);
      addPlaymat(ctx, 1, 0, -mz, Math.PI);
    },
  },
  {
    id: "pokemon",
    name: "Pokémon TCG",
    tagline: "Active and bench slots for two — import decks, set out six prizes, flip the coin.",
    category: "Trading card games",
    players: "2",
    accent: "#e8b23a",
    icon: "◓",
    table: { w: 190, d: 140 },
    actions: ["draw", "draw7", "prizes", "coin", "toGrave", "toHand"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 2));
      addTcgMat(ctx, POKE_MAT, 0, 0, 34, 0);
      addTcgMat(ctx, POKE_MAT, 1, 0, -34, Math.PI);
      // Shared damage-counter bank down the left, the flip coin on the right,
      // both just clear of the mats so the default camera framing shows them.
      DMG_COUNTERS.forEach((c, i) => {
        ctx.add({
          id: ctx.newId(),
          def: `dmgstack.${c.value}`,
          pos: { x: -42, y: 0, z: (i - 1) * 7 },
          yaw: 0,
          faceUp: true,
          contents: Array.from({ length: c.value === 10 ? 20 : c.value === 50 ? 12 : 8 }, (): Item => ({ def: `dmg.${c.value}` })),
        });
      });
      ctx.add({ id: ctx.newId(), def: "die.coin", pos: { x: 42, y: 0, z: 0 }, yaw: 0, faceUp: true, value: 1 });
    },
  },
  {
    id: "lorcana",
    name: "Lorcana",
    tagline: "Ink cards face-down, quest for lore — first to 20 on the lore tracker wins.",
    category: "Trading card games",
    players: "2",
    accent: "#8464d8",
    icon: "✦",
    table: { w: 190, d: 140 },
    life: { start: 0, seats: 2, commander: false },
    actions: ["draw", "draw7", "toInk", "toGrave", "toHand"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 2));
      addTcgMat(ctx, LORCANA_MAT, 0, 0, 34, 0);
      addTcgMat(ctx, LORCANA_MAT, 1, 0, -34, Math.PI);
    },
  },
  {
    id: "poker",
    name: "Poker Night",
    tagline: "A shuffled deck and a full chip bank, dealer's choice.",
    category: "Card games",
    players: "2–4",
    accent: "#1e8449",
    icon: "♣",
    table: { w: 170, d: 110 },
    actions: ["deal1", "deal5", "sortHand"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 4));
      addDeck52(ctx, 0, -10);
      addChipBank(ctx, -13, 6, 20);
    },
  },
  {
    id: "tricks",
    name: "Hearts & Spades",
    tagline: "Deal 13, play to your slot on the trick mat, winner takes the trick. Score pad included.",
    category: "Card games",
    players: "3–4",
    accent: "#c0392b",
    icon: "♥",
    table: { w: 170, d: 130 },
    life: { start: 0, seats: 4, commander: false },
    deal: 13,
    actions: ["dealAll", "takeTrick", "sortHand"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 4));
      ctx.add({
        id: "mat.trick",
        def: TRICK_MAT.def,
        pos: { x: 0, y: 0, z: 0 },
        yaw: 0,
        faceUp: true,
        locked: true,
      });
      for (const sl of TRICK_MAT.slots) {
        ctx.add({
          id: `slot.s${sl.seat}.trick`,
          def: "zone.slot",
          pos: { x: sl.lx, y: 0, z: sl.lz },
          yaw: sl.yaw,
          faceUp: true,
          locked: true,
          seat: sl.seat,
          slot: "trick",
        });
      }
      addDeck52(ctx, -TRICK_MAT.size / 2 - 12, 0);
    },
  },
  {
    id: "checkers",
    name: "Checkers",
    tagline: "Classic draughts — drop a piece on a piece to crown a king.",
    category: "Board games",
    players: "2",
    accent: "#7a5230",
    icon: "◉",
    table: { w: 130, d: 100 },
    build(ctx) {
      const board = ctx.add({
        id: ctx.newId(),
        def: CHECKERBOARD.def,
        pos: { x: 0, y: 0, z: 0 },
        yaw: 0,
        faceUp: true,
        locked: true,
      });
      const boardTop = board.pos.y * 2; // board rests at h/2, so top = h
      for (let row = 0; row < CHECKERBOARD.cells; row++) {
        if (row > 2 && row < 5) continue;
        for (let col = 0; col < CHECKERBOARD.cells; col++) {
          if ((col + row) % 2 !== 1) continue; // dark squares only (see CHECKERBOARD)
          const sq = checkerSquare(col, row);
          const color = row < 3 ? "black" : "red";
          ctx.add({
            id: ctx.newId(),
            def: `checker.${color}`,
            pos: { x: sq.x, y: boardTop + 0.4, z: sq.z },
            yaw: 0,
            faceUp: true,
          });
        }
      }
    },
  },
  {
    id: "backgammon",
    name: "Backgammon",
    tagline: "The classic race — standard start laid out, two dice a side, doubling cube on the bar.",
    category: "Board games",
    players: "2",
    accent: "#b3552e",
    icon: "◭",
    table: { w: 130, d: 100 },
    life: { start: 0, seats: 2, commander: false }, // match score
    build(ctx) {
      const board = ctx.add({
        id: ctx.newId(),
        def: BACKGAMMON.def,
        pos: { x: 0, y: 0, z: 0 },
        yaw: 0,
        faceUp: true,
        locked: true,
      });
      const boardTop = board.pos.y * 2; // board rests at h/2, so top = h
      // Standard start: south (red) on 24/13/8/6, north (black) mirrored.
      for (const [point, count] of BG_START) {
        for (let j = 0; j < count; j++) {
          for (const [color, p] of [["red", point], ["black", 25 - point]] as const) {
            const at = bgCheckerPos(p, j);
            ctx.add({
              id: ctx.newId(),
              def: `checker.${color}`,
              pos: { x: at.x, y: boardTop + getDef(`checker.${color}`).size[1] / 2, z: at.z },
              yaw: 0,
              faceUp: true,
            });
          }
        }
      }
      // Doubling cube on the bar, showing 64 (not yet doubled).
      const cube = getDef("die.doubling");
      ctx.add({
        id: ctx.newId(),
        def: "die.doubling",
        pos: { x: 0, y: boardTop + cube.size[1] / 2, z: 0 },
        yaw: 0,
        faceUp: true,
        value: cube.sides,
      });
      // Two d6 per player on the felt beside the board.
      for (const [x, z, value] of [[47, 5, 3], [47, -5, 5], [-47, 5, 2], [-47, -5, 4]] as const) {
        ctx.add({ id: ctx.newId(), def: "die.d6", pos: { x, y: 0, z }, yaw: 0, faceUp: true, value });
      }
    },
  },
  {
    id: "dominoes",
    name: "Dice & Dominoes",
    tagline: "A shuffled double-six boneyard, the full dice set, and a score pad — draw 7 and go.",
    category: "Board games",
    players: "2–4",
    accent: "#3fa08a",
    icon: "⚄",
    table: { w: 150, d: 110 },
    life: { start: 0, seats: 4, commander: false }, // score pad
    deal: 7,
    actions: ["dealAll", "draw"],
    build(ctx) {
      addHandZones(ctx, edgeHandZones(ctx.size, 4));
      ctx.add({
        id: ctx.newId(),
        def: "deck.domino",
        pos: { x: -26, y: 0, z: 0 },
        yaw: 0,
        faceUp: false,
        contents: dominoItems(ctx.rng),
      });
      DICE.forEach((die, i) => {
        ctx.add({
          id: ctx.newId(),
          def: `die.d${die.sides}`,
          pos: { x: 14 + i * 6, y: 0, z: -6 },
          yaw: 0,
          faceUp: true,
          value: die.sides,
        });
      });
    },
  },
  {
    id: "chess",
    name: "Chess",
    tagline: "The full 32-piece set on the board — white plays from the south seat.",
    category: "Board games",
    players: "2",
    accent: "#b8a67e",
    icon: "♞",
    table: { w: 130, d: 100 },
    build(ctx) {
      const board = ctx.add({
        id: ctx.newId(),
        def: CHECKERBOARD.def,
        pos: { x: 0, y: 0, z: 0 },
        yaw: 0,
        faceUp: true,
        locked: true,
      });
      const boardTop = board.pos.y * 2; // board rests at h/2, so top = h
      const place = (piece: ChessPieceKind, color: "white" | "black", col: number, row: number) => {
        const sq = checkerSquare(col, row);
        const h = CHESS_PIECES.find((p) => p.piece === piece)!.h;
        // Knights face the opponent (the model's head points along local +x).
        const yaw = piece !== "knight" ? 0 : color === "white" ? -Math.PI / 2 : Math.PI / 2;
        ctx.add({
          id: ctx.newId(),
          def: `chess.${color}.${piece}`,
          pos: { x: sq.x, y: boardTop + h / 2, z: sq.z },
          yaw,
          faceUp: true,
        });
      };
      CHESS_BACK_RANK.forEach((piece, col) => {
        place(piece, "black", col, 0);   // black's home rank at -z
        place(piece, "white", col, 7);   // white's at +z (south seat)
      });
      for (let col = 0; col < CHECKERBOARD.cells; col++) {
        place("pawn", "black", col, 1);
        place("pawn", "white", col, 6);
      }
    },
  },
];

export function getTableSpec(id: string): TableSpec {
  return TABLE_TYPES.find((t) => t.id === id) ?? TABLE_TYPES[0]!;
}

/** Build a fresh set of objects for a table type (used by Room). */
export function buildTable(spec: TableSpec, newId: () => string, rng: Rng): Map<string, GameObject> {
  const objects = new Map<string, GameObject>();
  const ctx: TableBuildCtx = {
    newId,
    rng,
    size: spec.table,
    add(o) {
      if (o.contents) o.count = o.contents.length;
      if (o.pos.y === 0) o.pos.y = restY(o);
      objects.set(o.id, o);
      return o;
    },
  };
  spec.build(ctx);
  return objects;
}
